import { useMemo, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import styled from "styled-components";
import { useIsAdmin } from "../../../hooks/useIsAdmin";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { Input } from "../../../components/Input";
import { MoneyInput } from "../../../components/MoneyInput";
import { Toggle } from "../../../components/Toggle";
import { SelectInput } from "../../../components/SelectInput";
import { InfoBox } from "../../../components/InfoBox";
import { formatMoney } from "../../../utils/formatMoney";
import { BillingItemEndButton } from "./BillingItemEndButton";
import { spacing, borderRadius } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { mapBillingPeriodToMonths } from "@KassaroAB/shared/utils/billingPeriod";
import { multiplyMultiCurrencyMoney } from "@KassaroAB/shared/utils/multiCurrencyMoney";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { RestUpdateBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import type { Money } from "@KassaroAB/shared/types/money";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import {
  getMinimumActiveUntilDate,
  getNonAdminMinimumEndDate,
  isDateBeforeMinimumActiveUntil,
  isEndDateBeforePaidUntil,
} from "../../../utils/billingItemMinimumActive";
import {
  getActiveSinceOnDate,
  getFirstSegmentStartDate,
  withSyncedSegmentDates,
} from "../../../utils/billingItemSegments";
import { formatDate } from "../../../utils/dateUtils";

const parseDateString = (s: string | null | undefined): string => {
  if (!s) return "";
  const dt = DateTime.fromISO(s);
  return dt.isValid ? dt.toISODate() ?? "" : "";
};

interface BillingItemPricing {
  monthlyPrice?: MultiCurrencyMoney;
  prepaid36MonthPrice?: MultiCurrencyMoney;
  oneTimePrice?: MultiCurrencyMoney;
}

interface BillingItemEditFormProps {
  billingItem: RestBillingItem;
  pricing?: BillingItemPricing;
  onSubmit: (data: RestUpdateBillingItem) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const PriceCard = styled.div`
  margin-bottom: ${spacing.x2}px;
  padding: ${spacing.x2}px;
  background-color: ${colors.backgroundDark};
  border-radius: ${borderRadius.small}px;
`;

const PriceTitle = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  margin-bottom: ${spacing.x1}px;
`;

const PriceRow = styled.div<{ $spacer?: boolean }>`
  font-size: ${fontSizes.small}px;
  color: ${colors.text};
  margin-bottom: ${spacing.x05}px;
  ${({ $spacer }) => $spacer && `margin-top: ${spacing.x05}px;`}
`;

const DateLockedMessage = styled.p`
  margin: 0 0 ${spacing.x2}px 0;
  color: ${colors.textMuted};
  font-size: ${fontSizes.regular}px;
`;

const formatPrice = (money?: MultiCurrencyMoney): string => {
  const sek = money?.currencies?.[Currency.SEK];
  if (!sek || sek.amount === 0) return "–";
  return formatMoney(sek);
};

const toDiscountAmountMajor = (discount: Money | null | undefined): number =>
  discount?.amount ? discount.amount / 100 : 0;

export const BillingItemEditForm = ({
  billingItem,
  pricing,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: BillingItemEditFormProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const { data: store } = useGetStoreQuery(storeId);
  const storeCurrency = store?.currency || Currency.SEK;
  const isAdmin = useIsAdmin();
  const now = DateTime.now();
  const today = now.startOf("day");
  const todayIso = today.toISODate() ?? "";
  const startDateDt = billingItem.startDate
    ? DateTime.fromISO(billingItem.startDate).startOf("day")
    : null;
  const startDateHasStarted = !!startDateDt && startDateDt <= today;
  const canEditStartDateAndPeriod = isAdmin || !startDateHasStarted;
  const [cancelError, setCancelError] = useState<Error | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const paidUntil = parseDateString(billingItem.paidUntil);
  const nonAdminMinEndDate = getNonAdminMinimumEndDate(paidUntil, todayIso);
  const paidUntilLimitsEndDate = !isAdmin && !!paidUntil && paidUntil > todayIso;

  const form = useForm({
    defaultValues: {
      startDate: parseDateString(billingItem.startDate),
      endDate: parseDateString(billingItem.endDate),
      billingPeriod: (billingItem.billingPeriod || BillingPeriod.MONTHLY),
      isDemo: !!billingItem.isDemo,
      paidUntil: parseDateString(billingItem.paidUntil),
      discountAmount: toDiscountAmountMajor(billingItem.discount),
    },
    onSubmit: ({ value }) => {
      setSaveError(null);
      const update: RestUpdateBillingItem = {};
      if (canEditStartDateAndPeriod && value.startDate) {
        const firstSegmentStartDate = getFirstSegmentStartDate(billingItem);
        if (
          value.startDate !== parseDateString(billingItem.startDate) ||
          (firstSegmentStartDate !== undefined &&
            value.startDate !== parseDateString(firstSegmentStartDate))
        ) {
          update.startDate = value.startDate;
        }
      }
      if (value.endDate !== parseDateString(billingItem.endDate)) {
        if (isAdmin || !value.endDate || value.endDate >= todayIso) {
          if (
            !isAdmin &&
            isEndDateBeforePaidUntil(value.endDate, paidUntil)
          ) {
            setSaveError(
              t("billingItems.edit.endDateBeforePaidUntil", {
                paidUntilDate: formatDate(paidUntil),
              })
            );
            return;
          }
          update.endDate = value.endDate || null;
        }
      }
      if (canEditStartDateAndPeriod) {
        const newBillingPeriod = value.billingPeriod || null;
        if (newBillingPeriod !== (billingItem.billingPeriod ?? null)) {
          update.billingPeriod = newBillingPeriod;
        }
      }
      if (isAdmin && value.isDemo !== !!billingItem.isDemo) {
        update.isDemo = value.isDemo;
      }
      if (isAdmin && value.paidUntil !== parseDateString(billingItem.paidUntil)) {
        update.paidUntil = value.paidUntil || null;
      }
      if (isAdmin) {
        const discountAmountMinor = Math.round((value.discountAmount || 0) * 100);
        const existingDiscountMinor = billingItem.discount?.amount ?? 0;
        if (discountAmountMinor !== existingDiscountMinor) {
          update.discount =
            discountAmountMinor > 0
              ? { amount: discountAmountMinor, currency: storeCurrency }
              : null;
        }
      }
      if (Object.keys(update).length > 0) {
        onSubmit(withSyncedSegmentDates(billingItem, update));
      } else {
        onCancel();
      }
    },
  });

  const billingPeriod = useStore(form.store, (state) => state.values.billingPeriod);
  const startDate = useStore(form.store, (state) => state.values.startDate);
  const endDate = useStore(form.store, (state) => state.values.endDate);
  const discountAmount = useStore(form.store, (state) => state.values.discountAmount);
  const activeSince = getActiveSinceOnDate(
    billingItem,
    startDate || billingItem.startDate
  );
  const minimumEndDate = getMinimumActiveUntilDate(billingItem.type, activeSince);
  const endDateBeforeMinimum = isDateBeforeMinimumActiveUntil(
    billingItem.type,
    activeSince,
    endDate
  );
  const endDateBeforePaidUntil =
    !isAdmin && isEndDateBeforePaidUntil(endDate, paidUntil);
  const periodMonths = mapBillingPeriodToMonths((billingPeriod || BillingPeriod.MONTHLY));
  const monthlyPrice = pricing?.monthlyPrice;
  const prepaid36Price = pricing?.prepaid36MonthPrice;
  const periodPrice = useMemo(() => {
    if (periodMonths >= 36 && prepaid36Price) {
      return multiplyMultiCurrencyMoney(prepaid36Price, periodMonths);
    }
    if (monthlyPrice && periodMonths > 0) {
      return multiplyMultiCurrencyMoney(monthlyPrice, periodMonths);
    }
    return undefined;
  }, [monthlyPrice, prepaid36Price, periodMonths]);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {pricing && (monthlyPrice || prepaid36Price || pricing.oneTimePrice) && (
        <PriceCard>
          <PriceTitle>Pris</PriceTitle>
          {monthlyPrice && (
            <PriceRow>
              Per månad: {formatPrice(monthlyPrice)}
            </PriceRow>
          )}
          {periodPrice && billingPeriod && periodMonths > 1 && (
            <PriceRow>
              Per period ({periodMonths} mån): {formatPrice(periodPrice)}
            </PriceRow>
          )}
          {prepaid36Price && (
            <PriceRow>
              Per månad vid 36+ mån avtal: {formatPrice(prepaid36Price)}
            </PriceRow>
          )}
          {pricing.oneTimePrice && (
            <PriceRow $spacer>
              Engångspris: {formatPrice(pricing.oneTimePrice)}
            </PriceRow>
          )}
          {discountAmount > 0 && monthlyPrice && (
            <>
              <PriceRow $spacer>
                {t("billingItems.edit.discountAmount")}: -
                {formatMoney({
                  amount: Math.round(discountAmount * 100),
                  currency: storeCurrency,
                })}
              </PriceRow>
              <PriceRow>
                {t("packageSelection.monthlyCostTotal")}
                {": "}
                {formatMoney({
                  amount: Math.max(
                    0,
                    (monthlyPrice.currencies?.[storeCurrency]?.amount ?? 0) -
                      Math.round(discountAmount * 100)
                  ),
                  currency: storeCurrency,
                })}
              </PriceRow>
            </>
          )}
        </PriceCard>
      )}
      {!canEditStartDateAndPeriod && (
        <DateLockedMessage>
          {t("billingItems.edit.startDateLocked")}
        </DateLockedMessage>
      )}
      <form.Field name="startDate">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("billingItems.table.startDate")}</FormLabel>
            <Input
              type="date"
              required
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              min={isAdmin ? undefined : todayIso}
              disabled={!canEditStartDateAndPeriod}
            />
          </FormGroup>
        )}
      </form.Field>
      <form.Field name="endDate">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("billingItems.table.endDate")}</FormLabel>
            <Input
              type="date"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              min={isAdmin ? undefined : nonAdminMinEndDate}
            />
          </FormGroup>
        )}
      </form.Field>
      {paidUntilLimitsEndDate && (
        <InfoBox variant="info">
          {t("billingItems.edit.endDatePaidUntilLimit", {
            paidUntilDate: formatDate(paidUntil),
          })}
        </InfoBox>
      )}
      {endDateBeforePaidUntil && (
        <InfoBox variant="error">
          {t("billingItems.edit.endDateBeforePaidUntil", {
            paidUntilDate: formatDate(paidUntil),
          })}
        </InfoBox>
      )}
      {endDateBeforeMinimum && minimumEndDate && (
        <InfoBox variant="warning">
          {t("billingItems.edit.endDateBeforeMinimum", {
            minimumDate: formatDate(minimumEndDate),
          })}
        </InfoBox>
      )}
      <form.Field name="billingPeriod">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("billingItems.table.invoicePeriod")}</FormLabel>
            <SelectInput
              value={field.state.value}
              onChange={(e) =>
                field.handleChange(e.target.value as BillingPeriod)
              }
              onBlur={field.handleBlur}
              disabled={!canEditStartDateAndPeriod}
            >
              {Object.values(BillingPeriod).map((period) => (
                <option key={period} value={period}>
                  {t(`billingItems.billingPeriod.${period}`)}
                </option>
              ))}
            </SelectInput>
          </FormGroup>
        )}
      </form.Field>
      {isAdmin && (
        <>
          <form.Field name="isDemo">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("billingItems.edit.isDemo")}</FormLabel>
                <Toggle
                  checked={field.state.value}
                  onChange={(checked) => field.handleChange(checked)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="paidUntil">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("billingItems.edit.paidUntil")}</FormLabel>
                <Input
                  type="date"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="discountAmount">
            {(field) => (
              <MoneyInput
                label={t("billingItems.edit.discountAmount")}
                amount={field.state.value}
                currency={storeCurrency}
                onAmountChange={(amount) =>
                  field.handleChange(Number.isFinite(amount) ? amount : 0)
                }
                onAmountBlur={field.handleBlur}
                placeholder="0"
              />
            )}
          </form.Field>
        </>
      )}
      {saveError && <InfoBox variant="error">{saveError}</InfoBox>}
      {cancelError && (
        <InfoBox variant="error">{cancelError.message}</InfoBox>
      )}
      <BillingItemEndButton
        availableBillingItemId={billingItem.availableBillingItemId}
        billingItemType={billingItem.type}
        activeSince={activeSince}
        paidUntil={paidUntil || null}
        isAdmin={isAdmin}
        onSuccess={onCancel}
        onError={setCancelError}
        disabled={isSubmitting}
      />
      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel} disabled={isSubmitting}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t("common.saving") : t("common.save")}
        </Button>
      </FormActions>
    </Form>
  );
};
